import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "react-query";
import ReportingService from "services/ReportingService";
import { REFETCH_DOWNLOAD_TIME } from "constants/ReportingsConstants";
import { FIRST_PAGE } from "constants/CommonConstants";
import { infiniteQueryConfig } from "queries/utils";
import { message } from "antd";
import {
  GET_CONTACT_BILLING_TRANSACTIONS,
  GET_CONTACT_PAYMENTS_STATS,
} from "queries/contacts";
import unpackAndShowErrors from "helpers/djangoErrorUnapacker";

export const TRANSACTIONS_KEY = "transactions";
export const DOWNLOAD_TRANSACTIONS_KEY = "transactionsDownload";
export const TRANSACTIONS_DOWNLOAD_RESULTS_KEY = "transactionsDownloadResults";
export const MARKETING_REPORTIN_KEY = "marketingReporting";
export const CAMPAIGN_PERFORMANCE_KEY = "campaignPerformance";
export const TREATMENTS_KEY = "treatments";
export const SMART_CONNECTIONS = "smart_connections";
export const PRODUCTS_EXTRA = "productsExtra";
export const TOTAL_APPT_SCHEDULED = "total_appt_scheduled";
export const SERVICE_RATE = "top_service_rate";
export const PROVIDERS_PRODUCTIVITY = "providers_productivity";
export const STAFF_UTILIZATION = "staff_utilization";
export const NO_SHOW_RATE = "no_show_rate";
export const LEAD_TIME_RATE = "lead_time_rate";
export const COMPLITED_RATE = "complited_rate";
export const RESCHEDULING_RATE = "rescheduling_rate";
export const PEAK_BOOKING_TIME = "peak_booking_time";
export const DOWNLOAD_SERVICE_REPORT_KEY = "service_report_download";
export const DOWNLOAD_SERVICE_REPORT_RESULTS =
  "service_report_download_results";
export const TRANSACTION_REPORTING_KEY = "transaction_reporting";
export const TRANSACTION_ITEM_SUMMARY_KEY = "transaction_item_summary";
export const TRANSACTION_EMPLOYEE_SALES_KEY = "transaction_employee_sales";

const useGetTransactions = (payload, options = {}) => {
  return useQuery({
    queryKey: [TRANSACTIONS_KEY, payload],
    queryFn: () => ReportingService.getTransactions(payload),
    keepPreviousData: true,
    ...options,
  });
};

const useDeleteTransaction = (successMsg, transactionPayload) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return ReportingService.deleteTransaction({ transactionId: payload });
    },
    onSuccess: () => {
      message.success(successMsg, 3);
      queryClient.invalidateQueries(TRANSACTIONS_KEY, [transactionPayload]);
    },
  });
};

const useGetTransaction = ({ transactionId, enabled }) =>
  useQuery({
    queryKey: [TRANSACTIONS_KEY, transactionId],
    queryFn: () => ReportingService.getSingleTransaction({ transactionId }),
    enabled: enabled,
  });

const useStartDownload = (payload) =>
  useQuery({
    queryKey: [DOWNLOAD_TRANSACTIONS_KEY, payload],
    queryFn: () => ReportingService.startTransactionsDownload(payload),
    enabled: false,
  });

const useGetDownloadResults = (
  downloadStarted,
  downloadLoading,
  downloadFetching
) =>
  useQuery({
    queryKey: [TRANSACTIONS_DOWNLOAD_RESULTS_KEY],
    queryFn: () => ReportingService.getTransactionsDownloadResults(),
    enabled: downloadStarted && !downloadLoading && !downloadFetching,
    refetchInterval: REFETCH_DOWNLOAD_TIME,
  });

const useGetMarketingReporting = (filter) => {
  return useQuery({
    queryKey: [MARKETING_REPORTIN_KEY, filter],
    queryFn: () => ReportingService.getMarketingReporting(filter),
    keepPreviousData: true,
  });
};

const useGetCampaignPerformance = () => {
  return useQuery({
    queryKey: [CAMPAIGN_PERFORMANCE_KEY],
    queryFn: () => ReportingService.getCampaignPefromance(),
    keepPreviousData: true,
  });
};

const useConnectContactToTransaction = (
  transactionPayload,
  successMsg,
  handleSuccess
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return ReportingService.connectContactAndTransaction(payload);
    },
    onSuccess: () => {
      message.success(successMsg, 3);
      transactionPayload &&
        queryClient.invalidateQueries(TRANSACTIONS_KEY, [transactionPayload]);
      queryClient.invalidateQueries(GET_CONTACT_BILLING_TRANSACTIONS);
      queryClient.invalidateQueries(GET_CONTACT_PAYMENTS_STATS);
      handleSuccess && handleSuccess();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};
const useRemoveTransactionFromContact = (transactionPayload, successMsg) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return ReportingService.removeContactAndTransaction(payload);
    },
    onSuccess: () => {
      message.success(successMsg, 3);
      transactionPayload &&
        queryClient.invalidateQueries(TRANSACTIONS_KEY, [transactionPayload]);
      queryClient.invalidateQueries(GET_CONTACT_BILLING_TRANSACTIONS);
      queryClient.invalidateQueries(GET_CONTACT_PAYMENTS_STATS);
    },
  });
};

const useGetTreatments = (payload) => {
  return useQuery({
    queryKey: [TREATMENTS_KEY, payload],
    queryFn: () => ReportingService.getTreatments(payload),
    keepPreviousData: true,
  });
};

const useGetSmartConnections = () => {
  return useQuery({
    queryKey: [SMART_CONNECTIONS],
    queryFn: () => ReportingService.getSmartConnections(),
  });
};

const useGetProductsExtra = (payload) => {
  return useQuery({
    queryKey: [PRODUCTS_EXTRA, payload],
    queryFn: () => ReportingService.getProductsExtra(payload),
    keepPreviousData: true,
  });
};

const useConnectTnxAndAppointment = (
  transactionPayload,
  handleClose,
  successMsg
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return ReportingService.connectAppointment(payload);
    },
    onSuccess: () => {
      message.success(successMsg, 3);
      transactionPayload &&
        queryClient.invalidateQueries(TRANSACTIONS_KEY, [transactionPayload]);
      handleClose();
    },
  });
};

const usePartialUpdateTransaction = (
  successMsg,
  successCallback,
  transactionPayload
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return ReportingService.partialUpdate(payload);
    },
    onSuccess: () => {
      message.success(successMsg, 3);
      successCallback();
      transactionPayload &&
        queryClient.invalidateQueries(TRANSACTIONS_KEY, [transactionPayload]);
    },
  });
};

const useGetTotalApptScheduled = (payload) => {
  return useQuery({
    queryKey: [TOTAL_APPT_SCHEDULED, payload],
    queryFn: () => ReportingService.getTotalApptScheduled(payload),
    keepPreviousData: true,
  });
};

const useGetServiceRate = (payload) => {
  return useQuery({
    queryKey: [SERVICE_RATE, payload],
    queryFn: () => ReportingService.getServiceRate(payload),
    keepPreviousData: true,
  });
};

const useProvidersProductivity = (payload) => {
  return useQuery({
    queryKey: [PROVIDERS_PRODUCTIVITY, payload],
    queryFn: () => ReportingService.getProvidersProductivity(payload),
    keepPreviousData: true,
  });
};
const useStaffUtilization = (payload) => {
  return useQuery({
    queryKey: [STAFF_UTILIZATION, payload],
    queryFn: () => ReportingService.getStaffUtalization(payload),
    keepPreviousData: true,
  });
};

const useNoShowRate = (payload) => {
  return useQuery({
    queryKey: [NO_SHOW_RATE, payload],
    queryFn: () => ReportingService.getNoShowRate(payload),
    keepPreviousData: true,
  });
};

const useLeadTimeRate = (payload) => {
  return useQuery({
    queryKey: [LEAD_TIME_RATE, payload],
    queryFn: () => ReportingService.getLeadTimeRate(payload),
    keepPreviousData: true,
  });
};
const useComplitedRate = (payload) => {
  return useQuery({
    queryKey: [COMPLITED_RATE, payload],
    queryFn: () => ReportingService.getComplitedRate(payload),
    keepPreviousData: true,
  });
};

const useReschedulingRate = (payload) => {
  return useQuery({
    queryKey: [RESCHEDULING_RATE, payload],
    queryFn: () => ReportingService.getReSchedulingRate(payload),
    keepPreviousData: true,
  });
};
const usePeakBookingTime = (payload) => {
  return useQuery({
    queryKey: [PEAK_BOOKING_TIME, payload],
    queryFn: () => ReportingService.getPeakBookkingTime(payload),
    keepPreviousData: true,
  });
};

const useStartDownloadServiceReport = (payload) =>
  useQuery({
    queryKey: [DOWNLOAD_SERVICE_REPORT_KEY, payload],
    queryFn: () => ReportingService.startServiceReportDownload(payload),
    enabled: false,
  });

const useGetDownloadServiceReportResults = (
  payload,
  downloadStarted,
  downloadLoading,
  downloadFetching
) =>
  useQuery({
    queryKey: [DOWNLOAD_SERVICE_REPORT_RESULTS],
    queryFn: () => ReportingService.getServiceReportDownloadResults(payload),
    enabled: downloadStarted && !downloadLoading && !downloadFetching,
    refetchInterval: REFETCH_DOWNLOAD_TIME,
  });

const useGetTransactionReporting = (payload, enabled = true) => {
  return useQuery({
    queryKey: [TRANSACTION_REPORTING_KEY, payload],
    queryFn: () => ReportingService.getTransactionReporting(payload),
    keepPreviousData: true,
    enabled,
  });
};

const useGetTransactionItemSummary = (payload, enabled = true) => {
  return useQuery({
    queryKey: [TRANSACTION_ITEM_SUMMARY_KEY, payload],
    queryFn: () => ReportingService.getTransactionItemSummary(payload),
    keepPreviousData: true,
    enabled,
  });
};

const useGetEmployeeSales = (payload, enabled = true) => {
  return useQuery({
    queryKey: [TRANSACTION_EMPLOYEE_SALES_KEY, payload],
    queryFn: () => ReportingService.getEmployeeSales(payload),
    keepPreviousData: true,
    enabled,
  });
};

const useInfiniteTransactionItemSummary = (
  payload,
  { enabled = true } = {}
) => {
  return useInfiniteQuery(
    [TRANSACTION_ITEM_SUMMARY_KEY, "infinite", payload],
    ({ pageParam = FIRST_PAGE }) =>
      ReportingService.getTransactionItemSummary({
        ...payload,
        page: pageParam,
      }),
    {
      ...infiniteQueryConfig,
      enabled,
    }
  );
};

export {
  useGetTransactions,
  useGetTransaction,
  useStartDownload,
  useGetTreatments,
  useGetDownloadResults,
  useGetMarketingReporting,
  useGetCampaignPerformance,
  useGetProductsExtra,
  useDeleteTransaction,
  useGetSmartConnections,
  usePartialUpdateTransaction,
  useConnectTnxAndAppointment,
  useConnectContactToTransaction,
  useRemoveTransactionFromContact,
  useGetTotalApptScheduled,
  useGetServiceRate,
  useProvidersProductivity,
  useStaffUtilization,
  useNoShowRate,
  useLeadTimeRate,
  useComplitedRate,
  useReschedulingRate,
  usePeakBookingTime,
  useStartDownloadServiceReport,
  useGetDownloadServiceReportResults,
  useGetTransactionReporting,
  useGetTransactionItemSummary,
  useInfiniteTransactionItemSummary,
  useGetEmployeeSales,
};
